// src/HistoryScreen.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUser } from './context/UserContext';
import { ArrowLeft, ChevronLeft, ChevronRight, Search, AlertCircle } from 'lucide-react';
import ImageModal from './components/ImageModal';
import ReportIssueModal from './components/ReportIssueModal'; 
import HistoryItem from './components/HistoryItem'; 


export default function HistoryScreen() {
  const navigate = useNavigate();
  const { user: currentUser } = useUser(); // Pega o usuário logado para verificar a role

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados dos filtros
  const [driverIdFilter, setDriverIdFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [opsIdFilter, setOpsIdFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedImage, setSelectedImage] = useState(null);
  const [showFilters, setShowFilters] = useState(false); // Estado para mostrar/ocultar filtros
  const [isReportModalVisible, setIsReportModalVisible] = useState(false); // --> NOVO
  
  const isAdmin = currentUser && (currentUser.role === 'admin-dev' || currentUser.role === 'admin-op');

  const fetchHistory = useCallback(async (pageToFetch) => {
    if (!currentUser?.id) {
      setLoading(false);
      setError("Usuário não encontrado. Faça o login novamente.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem('app_token');
      if (!token) throw new Error("Token de autenticação não encontrado.");

      const params = {
        page: pageToFetch,
        driver_id: driverIdFilter || undefined,
        data_filtro: dateFilter || undefined,
      };

      if (isAdmin && opsIdFilter) {
        params.ops_id_filter = opsIdFilter;
      }

      const response = await axios.get('/api/historico', { 
          params,
          headers: { 'Authorization': `Bearer ${token}` }
      });

      setHistory(response.data.data);
      setTotalPages(response.data.total_pages);
      setCurrentPage(response.data.page);
    } catch (err) {
      console.error("Erro ao buscar histórico:", err);
      setError(err.response?.data?.detail || "Não foi possível carregar o histórico.");
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser, driverIdFilter, dateFilter, opsIdFilter, isAdmin]);

  useEffect(() => {
    const initialFetch = () => {
        fetchHistory(1);
    }
    initialFetch();
  }, [fetchHistory]);
  
  const handleApplyFilter = () => {
    setCurrentPage(1);
    fetchHistory(1);
  };
  
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchHistory(newPage);
  }

  const clearFilters = () => {
    setDriverIdFilter("");
    setDateFilter("");
    setOpsIdFilter("");
    setCurrentPage(1);
    const fetchWithoutFilters = async () => {
        setLoading(true);
        try {
            const token = sessionStorage.getItem('app_token');
            const response = await axios.get('/api/historico', { 
                params: { page: 1 },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setHistory(response.data.data);
            setTotalPages(response.data.total_pages);
            setCurrentPage(response.data.page);
        } catch (err) {
            setError(err.response?.data?.detail || "Não foi possível carregar o histórico.");
        } finally {
            setLoading(false);
            setShowFilters(false);
        }
    }
    fetchWithoutFilters();
  };
  
  const renderContent = () => {
    if (loading) return <div className="text-center mt-10">Carregando...</div>;
    if (error) return <div className="text-center mt-10 text-red-500">{error}</div>;
    if (history.length === 0) return <div className="text-center mt-10 text-gray-500">Nenhum resultado encontrado.</div>;
    
    return (
      <div className="space-y-4">
        {history.map((item, index) => (
          <HistoryItem
            key={index}
            item={item}
            isAdmin={isAdmin}
            onImageClick={() => setSelectedImage(item.image_url)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="relative min-h-screen"> 
      <div className="bg-white p-6 flex flex-col min-h-screen">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate('/home')} className="p-2">
            <ArrowLeft size={24} className="text-[#EE4D2D]" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Histórico</h1>
          <div className="w-8"></div> {/* Espaçador para centralizar o título */}
        </div>

        {/* Barra de pesquisa */}
        <div className="flex flex-col mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)} // Alternar visibilidade dos filtros
            className="flex items-center bg-gray-100 text-gray-700 py-2 px-4 rounded-full hover:bg-gray-200 transition-colors w-full justify-center"
          >
            <Search size={18} className="mr-2" />
            <span>Filtrar</span>
          </button>

          {showFilters && ( // Mostrar/ocultar filtros
            <div className="bg-white p-4 border border-gray-200 rounded-lg mt-2 space-y-3">
                <input
                    type="text"
                    name="driverId"
                    placeholder="Driver ID"
                    value={driverIdFilter}
                    onChange={(e) => setDriverIdFilter(e.target.value)}
                    className="p-2 border rounded-lg w-full"
                />
              <div className="relative p-2 border rounded-lg w-full bg-white h-[42px]"> {/* Container que parece um input */}
                <input
                    type="date"
                    name="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                {/* Texto que mostra o valor selecionado ou o placeholder */}
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${dateFilter ? 'text-gray-700' : 'text-gray-400'}`}>
                  {dateFilter 
                    ? new Date(dateFilter + 'T00:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }) 
                    : 'Filtrar por data'}
                </span>
              </div>
                {isAdmin && (
                  <input
                    type="number"
                    placeholder="Filtrar por Ops ID"
                    value={opsIdFilter}
                    onChange={(e) => setOpsIdFilter(e.target.value)}
                    className="p-2 border rounded-lg w-full"
                  />
                )}
              <div className="flex items-center justify-end space-x-2 pt-2">
                <button onClick={clearFilters} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                  Limpar
                </button>
                <button onClick={handleApplyFilter} className="px-4 py-2 text-sm font-medium text-white bg-[#EE4D2D] rounded-lg hover:bg-[#D03F2A]">
                  Aplicar Filtros
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex-grow">
          {renderContent()}
        </div>

        {!loading && !error && history.length > 0 && totalPages > 1 && (
          <div className="flex justify-center items-center mt-6 py-4">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={24} />
            </button>
            <span className="mx-4 text-sm font-medium text-gray-700">
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}
      </div>

      <ImageModal imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
      
      <button
        onClick={() => setIsReportModalVisible(true)}
        className="fixed bottom-6 right-6 bg-[#EE4D2D] text-white p-3 rounded-full shadow-lg hover:bg-[#D03F2A] transition-all duration-300 z-40 opacity-80 hover:opacity-100"
        title="Relatar um Problema"
      >
        <AlertCircle size={24} />
      </button>

      <ReportIssueModal
        isVisible={isReportModalVisible}
        onClose={() => setIsReportModalVisible(false)}
        screenName="historico"
      />
    </div>
  );
}
